import { useEffect, useMemo, useRef, useState } from "react";
import { EventSoundtrack } from "../../../../redux/features/event/event.types";
import {
  getSoundtrackEmbedUrl,
  getSpotifyUri,
} from "../../../../utils/soundtrack";

type SpotifyIframeApi = {
  createController: (
    element: HTMLElement,
    options: { uri: string; width?: string | number; height?: string | number },
    callback: (controller: SpotifyController) => void,
  ) => void;
};

type SpotifyController = {
  addListener?: (
    event: "playback_update",
    callback: (event: {
      data?: {
        duration?: number;
        position?: number;
        isPaused?: boolean;
      };
    }) => void,
  ) => void;
  play?: () => void;
  pause?: () => void;
  resume?: () => void;
  seek?: (position: number) => void;
};

type SoundCloudWidget = {
  bind?: (event: string, callback: () => void) => void;
  play: () => void;
  pause: () => void;
  seekTo?: (position: number) => void;
  setVolume?: (volume: number) => void;
};

const SOUNDCLOUD_FINISH_EVENT = "finish";

type SoundCloudApi = {
  Widget: (iframe: HTMLIFrameElement) => SoundCloudWidget;
};

declare global {
  interface Window {
    onSpotifyIframeApiReady?: (api: SpotifyIframeApi) => void;
    __tikketSpotifyIframeApi?: SpotifyIframeApi;
    SC?: SoundCloudApi;
  }
}

const loadScript = (src: string, id: string, onLoad: () => void) => {
  const existingScript = document.getElementById(
    id,
  ) as HTMLScriptElement | null;

  if (existingScript) {
    if (existingScript.dataset.loaded === "true") {
      onLoad();
    } else {
      existingScript.addEventListener("load", onLoad, { once: true });
    }
    return;
  }

  const script = document.createElement("script");
  script.id = id;
  script.src = src;
  script.async = true;
  script.addEventListener(
    "load",
    () => {
      script.dataset.loaded = "true";
      onLoad();
    },
    { once: true },
  );
  document.body.appendChild(script);
};

const SoundOnIcon = () => (
  <svg
    aria-hidden
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 9.5V14.5H8L13 19V5L8 9.5H4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 9C16.8 9.8 17.25 10.86 17.25 12C17.25 13.14 16.8 14.2 16 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M18.5 6.5C20 8 20.75 9.86 20.75 12C20.75 14.14 20 16 18.5 17.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const SoundOffIcon = () => (
  <svg
    aria-hidden
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 9.5V14.5H8L13 19V5L8 9.5H4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 4L4 20"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const EventSoundtrackControl = ({
  soundtrack,
}: {
  soundtrack?: EventSoundtrack;
}) => {
  const [isMuted, setIsMuted] = useState(false);
  const spotifyContainerRef = useRef<HTMLDivElement | null>(null);
  const spotifyControllerRef = useRef<SpotifyController | null>(null);
  const soundCloudIframeRef = useRef<HTMLIFrameElement | null>(null);
  const soundCloudWidgetRef = useRef<SoundCloudWidget | null>(null);
  const pendingPlayRef = useRef(true);
  const hasStartedRef = useRef(false);
  const isMutedRef = useRef(false);
  const hasTriedGestureStartRef = useRef(false);

  const embedUrl = useMemo(
    () => getSoundtrackEmbedUrl(soundtrack),
    [soundtrack],
  );
  const spotifyUri = useMemo(() => getSpotifyUri(soundtrack), [soundtrack]);

  const startPlayback = () => {
    if (!soundtrack) return;

    const canControlPlayback =
      soundtrack.provider === "soundcloud"
        ? Boolean(soundCloudWidgetRef.current)
        : Boolean(spotifyControllerRef.current);

    if (!canControlPlayback) {
      pendingPlayRef.current = true;
      return;
    }

    isMutedRef.current = false;
    pendingPlayRef.current = false;
    hasStartedRef.current = true;

    if (soundtrack.provider === "soundcloud") {
      soundCloudWidgetRef.current?.setVolume?.(100);
      soundCloudWidgetRef.current?.play();
      return;
    }

    spotifyControllerRef.current?.resume?.();
    spotifyControllerRef.current?.play?.();
  };

  const mutePlayback = () => {
    if (!soundtrack) return;

    isMutedRef.current = true;

    if (soundtrack.provider === "soundcloud") {
      soundCloudWidgetRef.current?.setVolume?.(0);
      return;
    }

    // Spotify's iframe API does not expose volume, so pause is the closest
    // available mute equivalent while keeping the provider UI hidden.
    spotifyControllerRef.current?.pause?.();
  };

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    setIsMuted(false);
    isMutedRef.current = false;
    pendingPlayRef.current = true;
    hasStartedRef.current = false;
    hasTriedGestureStartRef.current = false;
    spotifyControllerRef.current = null;
    soundCloudWidgetRef.current = null;
  }, [soundtrack?.url]);

  useEffect(() => {
    if (!soundtrack) return;

    const startOnUserGesture = () => {
      if (hasTriedGestureStartRef.current) return;

      hasTriedGestureStartRef.current = true;
      if (isMutedRef.current) return;
      startPlayback();
    };

    window.addEventListener("pointerdown", startOnUserGesture, {
      capture: true,
      once: true,
    });
    window.addEventListener("touchstart", startOnUserGesture, {
      capture: true,
      once: true,
    });
    window.addEventListener("keydown", startOnUserGesture, {
      capture: true,
      once: true,
    });

    return () => {
      window.removeEventListener("pointerdown", startOnUserGesture, {
        capture: true,
      });
      window.removeEventListener("touchstart", startOnUserGesture, {
        capture: true,
      });
      window.removeEventListener("keydown", startOnUserGesture, {
        capture: true,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundtrack?.url]);

  useEffect(() => {
    if (!soundtrack || soundtrack.provider !== "spotify" || !spotifyUri) return;

    const createSpotifyController = (api: SpotifyIframeApi) => {
      if (!spotifyContainerRef.current) return;

      spotifyContainerRef.current.innerHTML = "";
      api.createController(
        spotifyContainerRef.current,
        { uri: spotifyUri, width: 1, height: 1 },
        (controller) => {
          spotifyControllerRef.current = controller;
          controller.addListener?.("playback_update", ({ data }) => {
            if (
              !hasStartedRef.current ||
              !data?.duration ||
              data.position === undefined ||
              data.isPaused ||
              data.position < data.duration - 1000
            ) {
              return;
            }

            controller.seek?.(0);
            controller.resume?.();
            controller.play?.();
          });

          if (pendingPlayRef.current && !isMutedRef.current) {
            startPlayback();
          }
        },
      );
    };

    window.onSpotifyIframeApiReady = (api) => {
      window.__tikketSpotifyIframeApi = api;
      createSpotifyController(api);
    };

    if (window.__tikketSpotifyIframeApi) {
      createSpotifyController(window.__tikketSpotifyIframeApi);
      return;
    }

    loadScript(
      "https://open.spotify.com/embed/iframe-api/v1",
      "spotify-iframe-api",
      () => {},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundtrack, spotifyUri]);

  useEffect(() => {
    if (!soundtrack || soundtrack.provider !== "soundcloud") return;

    loadScript(
      "https://w.soundcloud.com/player/api.js",
      "soundcloud-widget-api",
      () => {
        if (!soundCloudIframeRef.current || !window.SC) return;

        soundCloudWidgetRef.current = window.SC.Widget(
          soundCloudIframeRef.current,
        );
        soundCloudWidgetRef.current.bind?.(SOUNDCLOUD_FINISH_EVENT, () => {
          if (!hasStartedRef.current || !soundCloudWidgetRef.current) return;

          soundCloudWidgetRef.current.seekTo?.(0);
          soundCloudWidgetRef.current.play();
        });

        if (pendingPlayRef.current && !isMutedRef.current) {
          startPlayback();
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundtrack]);

  if (!soundtrack || !embedUrl) return null;

  const toggleSound = () => {
    if (isMuted) {
      setIsMuted(false);
      startPlayback();
      return;
    }

    setIsMuted(true);
    mutePlayback();
  };

  return (
    <div className="fixed bottom-6 left-6 z-40">
      <button
        type="button"
        onClick={toggleSound}
        aria-label={isMuted ? "Unmute soundtrack" : "Mute soundtrack"}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white shadow-def-cust transition hover:scale-[1.03] active:scale-95"
      >
        {isMuted ? <SoundOffIcon /> : <SoundOnIcon />}
      </button>

      {soundtrack.provider === "spotify" ? (
        <div
          ref={spotifyContainerRef}
          aria-hidden
          className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
        />
      ) : (
        <iframe
          ref={soundCloudIframeRef}
          title="Event soundtrack"
          src={embedUrl}
          allow="autoplay"
          aria-hidden
          className="pointer-events-none absolute h-px w-px border-0 opacity-0"
        />
      )}
    </div>
  );
};
